import React, { useCallback } from 'react';
import experiment from 'experiment';

import { RichText, Text } from '@wix/wix-base-ui';
import { SidePanel } from '@wix/design-system';
import { Hint } from '@wix/wix-ui-icons-common/classic-editor';

import { connectWithScope } from '#packages/apilib';
import { translate } from '#packages/i18n';
import * as baseUI from '#packages/baseUI'; // TODO: migrate to wds
import * as util from '#packages/util';

import HiddenElementsSection from './hiddenElementsSection/hiddenElementsSection';
import { HiddenElementsScope } from '../hiddenElementsScope';
import { mapStateToProps, mapDispatchToProps } from './mobileHiddenPanelMapper';

import styles from './mobileHiddenPanel.scss';

import type {
  MobileHiddenPanelStateProps,
  MobileHiddenPanelDispatchProps,
} from './mobileHiddenPanelMapper';

const isRedesignExperimentOpen =
  experiment.getValue('se_leftPanelHiddenMobileElementsRedesign') === 'B' ||
  experiment.getValue('se_leftPanelHiddenMobileElementsRedesign') === 'C';

interface MobileHiddenPanelProps
  extends MobileHiddenPanelStateProps,
    MobileHiddenPanelDispatchProps {}

const MobileHiddenPanel: React.FC<MobileHiddenPanelProps> = ({
  hiddenElementsTotalCount,
  masterPageHiddenElements,
  focusedPageHiddenElements,
  interactionsHiddenElements,
  focusedPageTitle,
  closePanel,
  showHelp,
  showLayersPanel,
}) => {
  const onLayersButtonClick = useCallback(() => {
    closePanel();
    showLayersPanel();
  }, [closePanel, showLayersPanel]);

  return (
    <SidePanel
      dataHook="mobile-hidden-panel"
      className={util.cx(styles.mobileHiddenPanel, {
        [styles.withRedesignExperiment]: isRedesignExperimentOpen,
      })}
      skin="floating"
      height="auto"
      onCloseButtonClick={closePanel}
      onHelpButtonClick={showHelp}
    >
      <SidePanel.Header
        title={`${translate(
          'mobile_hidden_items_panel_title',
        )} (${hiddenElementsTotalCount})`}
      />

      <SidePanel.Content noPadding>
        <SidePanel.Section titleVisible={false}>
          {hiddenElementsTotalCount > 0 ? (
            <div className={styles.description}>
              <Text size="small" weight="thin" enableEllipsis={false}>
                {'mobile_hidden_items_panel_description'}
              </Text>
            </div>
          ) : null}

          {masterPageHiddenElements.length > 0 ? (
            <HiddenElementsSection
              key="hiddenItemsMP"
              elements={masterPageHiddenElements}
              title={translate('mobile_hidden_items_section_title_allpages')}
              sectionType="masterPage"
            />
          ) : null}

          {focusedPageHiddenElements.length > 0 ||
          interactionsHiddenElements.length > 0 ? (
            <HiddenElementsSection
              key="hiddenItemsCP"
              elements={focusedPageHiddenElements}
              title={translate(
                'mobile_hidden_items_section_title_currentpage',
                {
                  PAGE: focusedPageTitle,
                },
              )}
              sectionType={'currentPage'}
            />
          ) : null}

          {hiddenElementsTotalCount === 0 ? (
            <div key="hiddenItemsNoItems" className={styles.noItems}>
              {translate('mobile_hidden_items_no_elements')}
            </div>
          ) : null}

          {interactionsHiddenElements.length > 0 && (
            <HiddenElementsSection elements={interactionsHiddenElements} />
          )}
        </SidePanel.Section>
      </SidePanel.Content>

      <SidePanel.Footer noPadding>
        <footer className={styles.footer}>
          {isRedesignExperimentOpen && <Hint />}
          <RichText className={styles.footerContent}>
            <div className={styles.footerTitle}>
              {!isRedesignExperimentOpen && (
                <baseUI.symbol name="infoSmall" className={styles.infoSmall} />
              )}
              <Text weight="bold" size="small">
                {'mobile_hidden_items_panel_layers_entry_title'}
              </Text>
            </div>
            <p>
              {translate('mobile_hidden_items_panel_layers_entry_text')}&nbsp;
              <a onClick={onLayersButtonClick}>
                {translate('mobile_hidden_items_panel_layers_entry_link')}
              </a>
            </p>
          </RichText>
        </footer>
      </SidePanel.Footer>
    </SidePanel>
  );
};

export default connectWithScope(
  () => HiddenElementsScope,
  MobileHiddenPanel,
  mapStateToProps,
  mapDispatchToProps,
);
